import { PriceField } from "@dashboard/components/PriceField";
import { buttonMessages } from "@dashboard/intl";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { prepareCheckoutMessages as messages } from "./messages";
import { isValidFreightAmount, usePreparedFreight } from "./usePreparedFreight";

export interface QuotedFreightFieldProps {
  checkoutId: string;
  currencyCode: string;
}

/** Set or clear the freight a rep quoted on the cart just prepared (WSM6-2572). */
export const QuotedFreightField = ({ checkoutId, currencyCode }: QuotedFreightFieldProps) => {
  const intl = useIntl();
  const { amount, saving, error, save, clear } = usePreparedFreight(checkoutId);
  const [draft, setDraft] = useState("");
  const current = amount
    ? currencyCode
      ? intl.formatNumber(Number(amount), { style: "currency", currency: currencyCode })
      : amount
    : null;

  return (
    <Box display="flex" flexDirection="column" gap={2} data-test-id="quoted-freight">
      <Text size={3} fontWeight="medium">
        {intl.formatMessage(messages.freightTitle)}
      </Text>
      <Text size={2} color="default2">
        {intl.formatMessage(messages.freightHint)}
      </Text>
      <Text size={2} data-test-id="quoted-freight-current">
        {current
          ? intl.formatMessage(messages.freightCurrent, { amount: current })
          : intl.formatMessage(messages.freightNone)}
      </Text>
      <Box display="flex" gap={2} alignItems="center">
        <PriceField
          name="freight"
          data-test-id="quoted-freight-input"
          label={intl.formatMessage(messages.freightTitle)}
          currencySymbol={currencyCode}
          min={0.01}
          value={draft}
          disabled={saving}
          onChange={event => setDraft(event.target.value == null ? "" : String(event.target.value))}
        />
        <Button
          variant="secondary"
          data-test-id="quoted-freight-save"
          disabled={saving || !isValidFreightAmount(draft)}
          onClick={() => save(draft)}
        >
          {intl.formatMessage(buttonMessages.save)}
        </Button>
        <Button
          variant="tertiary"
          data-test-id="quoted-freight-clear"
          disabled={saving || !amount}
          onClick={async () => {
            if (await clear()) {
              setDraft("");
            }
          }}
        >
          {intl.formatMessage(buttonMessages.clear)}
        </Button>
      </Box>
      {error && (
        <Text size={2} color="critical1" data-test-id="quoted-freight-error">
          {error}
        </Text>
      )}
    </Box>
  );
};

QuotedFreightField.displayName = "QuotedFreightField";
